import React, { useEffect, useState } from 'react';
import FacturaPreview, { printFactura } from './FacturaPreview';
import { api, formatCOP } from '../services/api';
import { shareFacturaPdf } from '../utils/facturaPdf';
import { enrichItemsWithImages } from '../utils/productImageLookup';
import { updateOfflinePedido } from '../utils/offlinePedidoQueue';

async function resolveMissingImages(items = []) {
  const base = enrichItemsWithImages(items);
  const out = [...base];
  await Promise.all(
    out.map(async (it, idx) => {
      if (it.imagen) return;
      const code = String(it.codigo || '').trim();
      const tryCodes = [];
      if (code && code !== '0') tryCodes.push(code);
      if (code === '0' || !code) tryCodes.push('0000000000000', '000000000000');
      for (const c of tryCodes) {
        try {
          const res = await api.getProducto(c);
          const img = res?.data?.imagen || null;
          if (img) {
            out[idx] = {
              ...it,
              codigo: res.data.codigo || c,
              imagen: img,
              nombre: it.nombre || res.data.nombre,
            };
            return;
          }
        } catch {
          /* next */
        }
      }
      // Último recurso: buscar por nombre en catálogo
      const q = String(it.nombre || '').trim();
      if (q.length >= 4) {
        try {
          const res = await api.getProductos({ q, limit: 8 });
          const hit = (res?.data || []).find(
            (p) => p.imagen && String(p.nombre || '').toLowerCase().includes(q.toLowerCase().slice(0, 12))
          );
          if (hit?.imagen) {
            out[idx] = {
              ...it,
              codigo: hit.codigo || it.codigo,
              imagen: hit.imagen,
            };
          }
        } catch {
          /* ignore */
        }
      }
    })
  );
  return out;
}

function fecha(value) {
  if (!value) return '';
  try {
    return new Date(value).toLocaleString('es-CO', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

function clienteFromEntry(entry) {
  const p = entry?.payload || {};
  return {
    nombre: p.cliente_nombre || entry?.cliente_nombre || 'Cliente',
    telefono: p.cliente_telefono || '',
    direccion: p.sucursal_direccion || '',
    notas: p.notas || '',
    vendedor: p.vendedor || '',
    sucursal_codigo: p.sucursal_codigo || '',
  };
}

function metaFromEntry(entry) {
  const p = entry?.payload || {};
  return {
    transportadora: p.transportadora || '',
    transportadora_otra: p.transportadora_otra || '',
    horario_recibo: p.horario_recibo || '',
    tipo_documento: p.tipo_documento ?? '',
    forma_pago: p.forma_pago ?? '',
  };
}

/**
 * Pedidos guardados en tablet (cotización / confirmación pendiente o sin red).
 */
export default function OfflinePedidoBanner({
  pending,
  syncing,
  online,
  queue = [],
  history = [],
  onSync,
  onClearHistory,
  /** Borrar un pedido pendiente de la tablet */
  onRemove,
  /** Controlled open from Dashboard "Guardados" */
  open: openProp = false,
  onOpenChange,
  /** Solo pendientes (aún no enviados a oficina) */
  onAgregarProductos,
  /** Abrir detalle de un local concreto (desde Mis pedidos) */
  focusEntryId = null,
  onFocusEntryConsumed,
}) {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = onOpenChange ? Boolean(openProp) : internalOpen;
  const setOpen = (next) => {
    if (onOpenChange) onOpenChange(next);
    else setInternalOpen(next);
  };
  const [detail, setDetail] = useState(null);
  const [detailItems, setDetailItems] = useState([]);
  const [sharing, setSharing] = useState(false);

  const detailIsPending = Boolean(
    detail && queue.some((q) => String(q.id) === String(detail.id))
  );

  // Abrir un pedido local desde Mis pedidos
  useEffect(() => {
    if (!focusEntryId) return undefined;
    const found = (queue || []).find((q) => String(q.id) === String(focusEntryId));
    if (found) {
      setOpen(true);
      setDetail(found);
    }
    onFocusEntryConsumed?.();
    return undefined;
  }, [focusEntryId, queue, onFocusEntryConsumed, setOpen]);

  useEffect(() => {
    if (!detail) {
      setDetailItems([]);
      return undefined;
    }
    let cancelled = false;
    const cached = enrichItemsWithImages(detail.payload?.items || []);
    setDetailItems(cached);
    (async () => {
      const resolved = await resolveMissingImages(detail.payload?.items || []);
      if (cancelled) return;
      setDetailItems(resolved);
      // Persist fotos en el guardado para la próxima vez
      const changed = resolved.some((it, i) => it.imagen && it.imagen !== (detail.payload?.items?.[i]?.imagen || null));
      if (changed && detail.id) {
        const nextPayload = {
          ...detail.payload,
          items: (detail.payload?.items || []).map((it, i) => ({
            ...it,
            imagen: resolved[i]?.imagen || it.imagen || null,
          })),
        };
        updateOfflinePedido(detail.id, { payload: nextPayload });
        setDetail((prev) => (prev && String(prev.id) === String(detail.id) ? { ...prev, payload: nextPayload } : prev));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [detail?.id]);


  useEffect(() => {
    if (!open) {
      setDetail(null);
      return undefined;
    }
    const onKey = (event) => {
      if (event.key === 'Escape') {
        if (detail) setDetail(null);
        else setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, detail]);

  const heldCount = queue.filter((q) => q.hold_until_manual).length;
  const offlineCount = pending - heldCount;
  const showBanner = pending > 0 || !online || history.length > 0;

  async function shareDetail(entry) {
    setSharing(true);
    try {
      await shareFacturaPdf({
        numero: entry.id,
        clienteNombre: entry.cliente_nombre,
        clienteTelefono: entry.payload?.cliente_telefono,
        telefono: entry.payload?.cliente_telefono,
        items: enrichItemsWithImages(entry.payload?.items || []),
        subtotal: entry.subtotal,
        offline: true,
      });
    } finally {
      setSharing(false);
    }
  }

  function handleAgregarProductos() {
    if (!detail || !detailIsPending || !onAgregarProductos) return;
    onAgregarProductos(detail);
    setDetail(null);
    setOpen(false);
  }

  function handleBorrar(entry) {
    if (!entry?.id || !onRemove) return;
    const nombre = entry.cliente_nombre || entry.payload?.cliente_nombre || 'este pedido';
    if (!window.confirm(`¿Borrar el pedido guardado de ${nombre}? No se puede deshacer.`)) return;
    onRemove(entry.id);
    if (detail && String(detail.id) === String(entry.id)) setDetail(null);
  }

  return (
    <>
      {showBanner ? (
        <div className={`offline-pedido-banner${!online ? ' offline-pedido-banner-down' : ''}`}>
          <div className="offline-pedido-banner-text">
            {!online ? (
              <>
                <strong>Sin internet</strong>
                <span>
                  {pending
                    ? `${pending} pedido${pending === 1 ? '' : 's'} en la tablet.`
                    : 'Puedes armar pedidos; se guardarán hasta que haya señal.'}
                </span>
              </>
            ) : pending ? (
              <>
                <strong>
                  {pending} pedido{pending === 1 ? '' : 's'} guardado
                  {pending === 1 ? '' : 's'} en tablet
                </strong>
                <span>
                  {heldCount
                    ? 'Esperando cotización / confirmación del cliente. Ábrelos con Guardados o Enviar a oficina cuando confirmen.'
                    : offlineCount
                      ? 'Quedaron sin señal; se pueden enviar ahora.'
                      : 'Toca Ver / Enviar cuando quieras mandarlos a oficina.'}
                </span>
              </>
            ) : (
              <>
                <strong>Pedidos enviados desde tablet</strong>
                <span>
                  {history.length} envío{history.length === 1 ? '' : 's'} reciente
                  {history.length === 1 ? '' : 's'}
                </span>
              </>
            )}
          </div>
          <div className="offline-pedido-actions">
            <button type="button" className="offline-pedido-view" onClick={() => setOpen(true)}>
              Ver pedidos
            </button>
            {pending > 0 && online ? (
              <button type="button" className="offline-pedido-sync" disabled={syncing} onClick={onSync}>
                {syncing ? 'Enviando…' : 'Enviar a oficina'}
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      {open ? (
        <div className="offline-outbox-backdrop" role="presentation" onMouseDown={() => setOpen(false)}>
          <section
            className="offline-outbox"
            role="dialog"
            aria-modal="true"
            aria-labelledby="offline-outbox-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <header className="offline-outbox-head">
              <div>
                <h2 id="offline-outbox-title">Pedidos guardados</h2>
                <p>Cotización / confirmación en tablet — aún no van a oficina</p>
              </div>
              <button
                type="button"
                className="icon-btn"
                onClick={() => setOpen(false)}
                aria-label="Cerrar"
                autoFocus
              >
                ×
              </button>
            </header>

            <div className="offline-outbox-body">
              <h3>Pendientes ({queue.length})</h3>
              {!queue.length ? (
                <p className="offline-outbox-empty">No hay pedidos guardados en la tablet.</p>
              ) : null}
              {queue.map((item) => (
                <div
                  className={`offline-outbox-item offline-outbox-item-row${item.last_error ? ' has-error' : ''}`}
                  key={item.id}
                >
                  <button
                    type="button"
                    className="offline-outbox-item-btn"
                    onClick={() => setDetail(item)}
                  >
                    <div>
                      <strong>{item.cliente_nombre}</strong>
                      <span>
                        {item.items_count} producto{item.items_count === 1 ? '' : 's'}
                        {item.subtotal != null ? ` · ${formatCOP(item.subtotal)}` : ''}
                      </span>
                      <small>{fecha(item.created_at)}</small>
                      <span className="offline-outbox-ver">Toca para ver el pedido completo →</span>
                    </div>
                    <span className={`offline-status ${item.last_error ? 'error' : 'pending'}`}>
                      {item.last_error
                        ? 'Requiere revisión'
                        : item.hold_until_manual
                          ? 'Cotización'
                          : 'Por enviar'}
                    </span>
                    {item.last_error ? <p>{item.last_error}</p> : null}
                  </button>
                  {onRemove ? (
                    <button
                      type="button"
                      className="offline-outbox-borrar"
                      onClick={() => handleBorrar(item)}
                      aria-label={`Borrar pedido de ${item.cliente_nombre || 'cliente'}`}
                    >
                      Borrar
                    </button>
                  ) : null}
                </div>
              ))}

              <div className="offline-outbox-title-row">
                <h3>Enviados a oficina ({history.length})</h3>
                {history.length ? (
                  <button type="button" className="link-btn" onClick={onClearHistory}>
                    Limpiar historial
                  </button>
                ) : null}
              </div>
              {!history.length ? (
                <p className="offline-outbox-empty">Todavía no hay envíos desde esta lista.</p>
              ) : null}
              {history.map((item) => (
                <article className="offline-outbox-item sent" key={`${item.id}-${item.sent_at}`}>
                  <div>
                    <strong>{item.numero || item.cliente_nombre}</strong>
                    <span>{item.cliente_nombre}</span>
                    <small>Enviado {fecha(item.sent_at)}</small>
                  </div>
                  <span className="offline-status sent">Enviado</span>
                </article>
              ))}
            </div>

            <footer className="offline-outbox-foot">
              {pending > 0 && online ? (
                <button type="button" className="primary-btn" disabled={syncing} onClick={onSync}>
                  {syncing ? 'Enviando…' : 'Enviar pendientes a oficina'}
                </button>
              ) : null}
              <button type="button" className="ghost-btn" onClick={() => setOpen(false)}>
                Cerrar
              </button>
            </footer>
          </section>
        </div>
      ) : null}

      {detail ? (
        <div
          className="offline-outbox-backdrop offline-detail-backdrop"
          role="presentation"
          onMouseDown={() => setDetail(null)}
        >
          <section
            className="offline-outbox offline-detail"
            role="dialog"
            aria-modal="true"
            aria-label="Detalle del pedido guardado"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <header className="offline-outbox-head">
              <div>
                <h2>Pedido guardado</h2>
                <p>
                  {detail.hold_until_manual
                    ? 'En tablet — cotización / confirmación (aún no en oficina)'
                    : 'En tablet — pendiente de enviar a oficina'}
                </p>
              </div>
              <button type="button" className="icon-btn" onClick={() => setDetail(null)} aria-label="Cerrar">
                ×
              </button>
            </header>
            <div className="offline-outbox-body offline-detail-body" id="guardado-factura-print">
              <FacturaPreview
                titulo="Pedido en tablet"
                numero={null}
                cliente={clienteFromEntry(detail)}
                pedidoMeta={metaFromEntry(detail)}
                pedido={{}}
                items={detailItems}
                subtotal={detail.subtotal}
              />
            </div>
            <footer className="offline-outbox-foot offline-detail-foot">
              {detailIsPending && onAgregarProductos ? (
                <button type="button" className="btn-agregar-productos" onClick={handleAgregarProductos}>
                  + Agregar productos
                </button>
              ) : null}
              {detailIsPending && onRemove ? (
                <button type="button" className="btn-borrar-guardado" onClick={() => handleBorrar(detail)}>
                  Borrar
                </button>
              ) : null}
              <button
                type="button"
                className="ghost-btn"
                onClick={() => printFactura('guardado-factura-print')}
              >
                Imprimir / PDF
              </button>
              <button
                type="button"
                className="btn-whatsapp"
                disabled={sharing}
                onClick={() => shareDetail(detail)}
              >
                {sharing ? 'Preparando…' : 'WhatsApp (PDF)'}
              </button>
              <button type="button" className="ghost-btn" onClick={() => setDetail(null)}>
                Volver
              </button>
            </footer>
          </section>
        </div>
      ) : null}
    </>
  );
}
